import React from 'react';
import { Link } from 'react-router-dom';
import { Download, Mail, Phone, Linkedin, CheckCircle, FileText } from 'lucide-react';
import { mediaKitContent, speakingTopics, testimonials } from '../mockData';
import SEO from '../components/SEO';

const MediaKit = () => {
  // Handler for PDF download (placeholder - will be updated when PDF upload feature is added)
  const handleDownloadPDF = () => {
    // Latest PDF - v2 April 2026
    window.open('https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/ml98uhlx_Ravi%20Prakash%20-%20Speaker%20Media%20Kit-v2.pdf', '_blank');
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Media Kit - Speaker Resources & Photos"
        description="Download Ravi Prakash's speaker media kit, high-resolution photos, bio, speaking topics, testimonials, and video clips. Everything you need to promote your event featuring this TEDx speaker and leadership expert."
        canonical="/media-kit"
      />

      {/* Hero Section */}
      <section className="bg-safar-blue text-white section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="heading-xl text-white mb-6">
            Speaker Media Kit
          </h1>
          <p className="text-xl font-sans text-gray-100 mb-8 leading-relaxed max-w-3xl mx-auto">
            Everything event organizers need to evaluate, share, and book Ravi Prakash for keynote speaking, fireside chats, panels, graduations, and book signing events.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleDownloadPDF}
              className="bg-white text-safar-blue hover:bg-gray-100 font-sans font-semibold px-8 py-4 rounded-lg transition-colors inline-flex items-center justify-center"
            >
              <Download className="mr-2 h-5 w-5" />
              Download PDF Media Kit
            </button>
            <a 
              href="https://calendly.com/journeywithravi/30" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-safar-blue font-sans font-semibold px-8 py-4 rounded-lg transition-colors"
            >
              Book Ravi
            </a>
          </div>
          
          <p className="text-sm text-gray-200 mt-6 font-sans">
            Includes bio, speaking topics, photos, and event information.
          </p>
        </div>
      </section>

      {/* For Event Planners */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-8">
            For event planners and organizers
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <p className="text-lg font-sans text-gray-800 leading-relaxed">
              Use this media kit to review Ravi's speaking topics, biography, event fit, photos, and booking information.
            </p>
            <p className="text-lg font-sans text-gray-800 leading-relaxed">
              If you are planning an event and want a speaker who combines leadership insight, lived experience, and a deeply human message, this is the place to start.
            </p>
          </div>
        </div>
      </section>

      {/* Who I Am */}
      <section className="bg-white section-padding">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
            {/* Photo */}
            <div className="lg:col-span-1">
              <img
                src="https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/3fgycr2n_Ravi%20Book-3_HiRes-cropped%20headshot.jpg"
                alt="Ravi Prakash"
                className="w-full rounded-lg shadow-xl"
              />
            </div>
            
            {/* Bio */}
            <div className="lg:col-span-2">
              <h2 className="heading-md text-charcoal mb-6">
                Who I Am
              </h2>
              
              <div className="space-y-4">
                <p className="text-base font-sans text-gray-800 leading-relaxed">
                  {mediaKitContent.bio}
                </p>
                
                <div className="bg-soft-stone rounded-lg p-6 mt-6">
                  <h3 className="text-xl font-serif font-semibold text-charcoal mb-4">
                    My Journey
                  </h3>
                  <p className="text-base font-sans text-gray-800 leading-relaxed">
                    {mediaKitContent.journey}
                  </p>
                </div>
                
                <div className="bg-safar-blue bg-opacity-10 rounded-lg p-6 border-l-4 border-safar-blue">
                  <h3 className="text-xl font-serif font-semibold text-charcoal mb-4">
                    Speaking Philosophy
                  </h3>
                  <p className="text-lg font-sans font-semibold text-gray-800">
                    {mediaKitContent.philosophy}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* USPs */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-12">
            What Sets Ravi Apart
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mediaKitContent.usps.map((usp) => (
              <div
                key={usp.title}
                className="bg-white rounded-lg p-6 text-center shadow-md"
              >
                <div className="w-12 h-12 bg-safar-orange rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-serif font-semibold text-charcoal mb-2">
                  {usp.title}
                </h3>
                <p className="text-sm font-sans text-gray-700">
                  {usp.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Speaking Topics */}
      <section className="bg-white section-padding">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-12">
            Core Speaking Themes
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {speakingTopics.map((topic) => (
              <div
                key={topic.id}
                className="bg-soft-white border border-gray-200 rounded-lg p-6"
              >
                <h3 className="text-2xl font-serif font-semibold text-charcoal mb-3">
                  {topic.title}
                </h3>
                <p className="text-base font-sans text-gray-700 mb-4 leading-relaxed">
                  {topic.shortDescription}
                </p>
                <p className="text-sm font-sans font-medium text-safar-blue">
                  {topic.bestFor}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-12">
            What People Say
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.slice(0, 4).map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white rounded-lg p-6 shadow-md"
              >
                <p className="text-base font-sans text-gray-800 italic mb-4 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-gray-200 pt-3">
                  <p className="font-sans font-semibold text-charcoal text-sm">
                    {testimonial.author}
                  </p>
                  <p className="text-xs font-sans text-gray-600">
                    {testimonial.title}, {testimonial.organization}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Availability */}
      <section className="bg-white section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-8">
            Booking Information
          </h2>
          
          <div className="bg-soft-stone rounded-lg p-8 max-w-3xl mx-auto">
            <h3 className="text-2xl font-serif font-semibold text-charcoal mb-6 text-center">
              Available Worldwide
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {[
                'Corporate Keynotes',
                'Leadership Summits',
                'University Lectures',
                'Graduations',
                'Fireside Chats',
                'Panel Discussions',
                'Book Signing Events',
                'VIP Meet and Greet'
              ].map((format) => (
                <div
                  key={format}
                  className="flex items-center bg-white rounded p-3"
                >
                  <CheckCircle className="h-5 w-5 text-safar-orange mr-3 flex-shrink-0" />
                  <span className="text-base font-sans text-gray-800">{format}</span>
                </div>
              ))}
            </div>
            
            <p className="text-center text-sm font-sans text-gray-600 italic">
              In-person and virtual options available
            </p>
          </div>
        </div>
      </section>

      {/* Videos Section */}
      <section className="bg-white section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-12">
            Speaking Videos
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* TEDx Video */}
            <div>
              <div className="relative rounded-lg overflow-hidden shadow-lg bg-black">
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/ULNDVkxUxOc"
                    title="TEDx Talk: Self-Doubt to Confidence"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  ></iframe>
                </div>
              </div>
              <div className="mt-3">
                <h4 className="text-lg font-serif font-semibold text-charcoal mb-1">
                  TEDx: Self-Doubt to Confidence
                </h4>
                <p className="text-sm font-sans text-gray-600 mb-1">TEDx Lake Mary</p>
                <p className="text-xs font-sans font-semibold text-safar-blue">>59K views</p>
              </div>
            </div>

            {/* Podcast */}
            <div>
              <div className="relative rounded-lg overflow-hidden shadow-lg bg-black">
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/N31Um6aBMks"
                    title="Dare to Declare Podcast"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  ></iframe>
                </div>
              </div>
              <div className="mt-3">
                <h4 className="text-lg font-serif font-semibold text-charcoal mb-1">
                  224 Rejections and One Yes
                </h4>
                <p className="text-sm font-sans text-gray-600">Dare to Declare Podcast</p>
              </div>
            </div>

            {/* Ramaiah Institute */}
            <div>
              <div className="relative rounded-lg overflow-hidden shadow-lg bg-black">
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/WQO9Uye1L3E"
                    title="Leadership Talk at Ramaiah Institute"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  ></iframe>
                </div>
              </div>
              <div className="mt-3">
                <h4 className="text-lg font-serif font-semibold text-charcoal mb-1">
                  Leading Yourself: Life & Goal Setting
                </h4>
                <p className="text-sm font-sans text-gray-600">Ramaiah Institute, Bangalore</p>
              </div>
            </div>
          </div>

          <p className="text-center text-sm font-sans text-gray-600 italic mt-8">
            Full-length keynote recordings available upon request
          </p>
        </div>
      </section>

      {/* Video Shorts - Quick Insights Section */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-charcoal mb-4">
              Quick Insights - Video Shorts
            </h2>
            <p className="text-lg font-sans text-gray-700 max-w-3xl mx-auto">
              Powerful leadership lessons in short clips. Perfect for social media sharing, event promotion, and giving your audience a taste of Ravi's message.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Video 1: Develop Unshakable Faith */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <div className="relative bg-black aspect-[9/16]">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/69SlF1VwWkA"
                  title="Develop Unshakable Faith"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-4">
                <h4 className="text-base font-serif font-semibold text-charcoal mb-2 leading-tight">
                  Develop Unshakable Faith
                </h4>
                <p className="text-xs font-sans text-gray-600">
                  Lessons from Life's Toughest School
                </p>
              </div>
            </div>

            {/* Video 2: Embrace the Mess */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <div className="relative bg-black aspect-[9/16]">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/QyW0pO4VpWs"
                  title="Embrace the Mess"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-4">
                <h4 className="text-base font-serif font-semibold text-charcoal mb-2 leading-tight">
                  Embrace the Mess
                </h4>
                <p className="text-xs font-sans text-gray-600">
                  Find Your Masterpiece in Life
                </p>
              </div>
            </div>

            {/* Video 3: Kindness Changes Lives */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <div className="relative bg-black aspect-[9/16]">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/1tFRoyAgebc"
                  title="Kindness Changes Lives"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-4">
                <h4 className="text-base font-serif font-semibold text-charcoal mb-2 leading-tight">
                  Kindness Changes Lives
                </h4>
                <p className="text-xs font-sans text-gray-600">
                  From Struggle to Success Story
                </p>
              </div>
            </div>

            {/* Video 4: Mindset & Comfort Zone */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <div className="relative bg-black aspect-[9/16]">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/ziLgiqyiSmc"
                  title="Mindset & Comfort Zone"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-4">
                <h4 className="text-base font-serif font-semibold text-charcoal mb-2 leading-tight">
                  Mindset & Comfort Zone
                </h4>
                <p className="text-xs font-sans text-gray-600">
                  Unleash Your Untapped Potential
                </p>
              </div>
            </div>

            {/* Video 5: Never Too Late */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <div className="relative bg-black aspect-[9/16]">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/NPHWxvYbrqY"
                  title="Never Too Late"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-4">
                <h4 className="text-base font-serif font-semibold text-charcoal mb-2 leading-tight">
                  Never Too Late
                </h4>
                <p className="text-xs font-sans text-gray-600">
                  Design Your Profound Next Chapter
                </p>
              </div>
            </div>

            {/* Video 6: Success Secrets */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <div className="relative bg-black aspect-[9/16]">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/Z5q6ZOsEirk"
                  title="Success Secrets"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-4">
                <h4 className="text-base font-serif font-semibold text-charcoal mb-2 leading-tight">
                  Success Secrets
                </h4>
                <p className="text-xs font-sans text-gray-600">
                  Transform Failure into Your Fuel
                </p>
              </div>
            </div>

            {/* Video 7: Unleash Your Inner Diamond */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <div className="relative bg-black aspect-[9/16]">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/2ns-vNo2jBw"
                  title="Unleash Your Inner Diamond"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-4">
                <h4 className="text-base font-serif font-semibold text-charcoal mb-2 leading-tight">
                  Unleash Your Inner Diamond
                </h4>
                <p className="text-xs font-sans text-gray-600">
                  Age is Just a Number
                </p>
              </div>
            </div>

            {/* Video 8: Unlock Your Potential */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <div className="relative bg-black aspect-[9/16]">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/xwqiufZ14fY"
                  title="Unlock Your Potential"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-4">
                <h4 className="text-base font-serif font-semibold text-charcoal mb-2 leading-tight">
                  Unlock Your Potential
                </h4>
                <p className="text-xs font-sans text-gray-600">
                  Take the Next Step Fearlessly
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <p className="text-sm font-sans text-gray-600 italic">
              All video shorts • Perfect for social media sharing, event promotion, and audience preview
            </p>
          </div>
        </div>
      </section>

      {/* Photos Section */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-12">
            Speaker Photos
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/3fgycr2n_Ravi%20Book-3_HiRes-cropped%20headshot.jpg"
                alt="Ravi Prakash Professional Headshot"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/v9wxtm0g_Ravi-Prakash-10-holding%20suitecase%20above%20my%20head.jpg"
                alt="Ravi with Suitcase"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/cnxbczhe__DSC3880.jpg"
                alt="Ravi Speaking on Stage"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <p className="text-center text-sm font-sans text-gray-600 mt-6">
            High-resolution images available upon request
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="bg-white section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-12">
            Contact & Booking
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <Link
              to="/book-ravi"
              className="flex flex-col items-center p-8 bg-soft-stone rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Mail className="h-10 w-10 text-safar-blue mb-4" />
              <p className="text-lg font-sans font-semibold text-charcoal mb-2">Book Ravi</p>
              <p className="text-sm font-sans text-gray-700 text-center">
                Inquire about speaking engagements
              </p>
            </Link>
            
            <a
              href="https://www.linkedin.com/in/journeywithravi/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-8 bg-soft-stone rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Linkedin className="h-10 w-10 text-safar-blue mb-4" />
              <p className="text-lg font-sans font-semibold text-charcoal mb-2">LinkedIn</p>
              <p className="text-sm font-sans text-gray-700 text-center">Connect & Follow</p>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-near-black text-white section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-lg text-white mb-8">
            Ready to discuss your event?
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-ravi" className="bg-safar-orange text-white hover:bg-burnt-orange font-sans font-semibold px-8 py-4 rounded-lg transition-colors">
              Book Ravi
            </Link>
            <button
              onClick={handleDownloadPDF}
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-near-black font-sans font-semibold px-8 py-4 rounded-lg transition-colors inline-flex items-center justify-center"
            >
              <Download className="mr-2 h-5 w-5" />
              Download PDF Kit
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MediaKit;
